import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { BACKEND_URL } from '../redux/api/axiosManager';

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

function getSocketAuthToken(tokens) {
  if (!tokens) return null;
  return tokens.accessToken
    || tokens.access?.token
    || tokens.access_token
    || tokens.token
    || null;
}

function buildAdminClientInfo(user) {
  return {
    clientType: 'admin-dashboard',
    userId: user?.id ?? user?.userId ?? null,
    userInfo: {
      platform: 'web',
      email: user?.email ?? null,
      timestamp: new Date().toISOString(),
    },
  };
}

export const SocketProvider = ({ children }) => {
  const { user, tokens, isAuthenticated } = useSelector((state) => state.auth);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const authRef = useRef({ user, tokens, isAuthenticated });

  authRef.current = { user, tokens, isAuthenticated };

  useEffect(() => {
    if (!BACKEND_URL) {
      return undefined;
    }

    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      auth: (cb) => {
        const token = getSocketAuthToken(authRef.current.tokens);
        cb({
          token,
          clientType: 'admin-dashboard',
        });
      },
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      newSocket.emit('update-client-info', buildAdminClientInfo(authRef.current.user));
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', () => {
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Login sonrası admin-dashboard kaydını yenile (ilk connect userId:null olabiliyordu)
  useEffect(() => {
    if (!socket || !isConnected || !isAuthenticated) return;

    socket.emit('update-client-info', buildAdminClientInfo(user));
  }, [socket, isConnected, isAuthenticated, user?.id, user?.email]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};
